import { jsPDF } from 'jspdf';
import { toPng } from 'html-to-image';
import type { MonthlySalesResponse } from '../api/BookingApi';
import { formatPrice } from './formatPrice';

const RED = '#dc2626';
const SLATE_900 = '#0f172a';
const SLATE_500 = '#64748b';
const SLATE_200 = '#e2e8f0';
const SLATE_50 = '#f8fafc';
const WHITE = '#ffffff';
const EMERALD = '#059669';
const ROSE = '#e11d48';

export type DashboardChartRefs = {
  topServices: HTMLElement | null;
  paymentPie: HTMLElement | null;
  staffPerformance: HTMLElement | null;
  paymentBreakdown: HTMLElement | null;
};

export type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

export type GenerateDashboardPdfParams = {
  sales: MonthlySalesResponse;
  totalUsers: number;
  totalCustomers: number;
  totalServices: number;
  month: number;
  year: number;
  chartRefs: DashboardChartRefs;
  t: TranslateFn;
};

function deltaPercent(current: number, previous: number): { value: string; positive: boolean } | null {
  if (previous === 0 && current === 0) return null;
  if (previous === 0) return { value: '+100%', positive: true };
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  const sign = pct >= 0 ? '+' : '';
  return { value: `${sign}${pct.toFixed(1)}%`, positive: pct >= 0 };
}

async function loadImageAsDataUrl(src: string): Promise<string | null> {
  try {
    const res = await fetch(src);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

async function captureChart(el: HTMLElement | null): Promise<string | null> {
  if (!el) return null;
  try {
    return await toPng(el, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: WHITE,
    });
  } catch {
    return null;
  }
}

function drawRoundedRect(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  fill: string,
  stroke?: string,
) {
  doc.setFillColor(fill);
  if (stroke) {
    doc.setDrawColor(stroke);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, w, h, r, r, 'FD');
  } else {
    doc.roundedRect(x, y, w, h, r, r, 'F');
  }
}

function drawStatCard(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  title: string,
  value: string,
  hint?: string,
  delta?: { value: string; positive: boolean } | null,
) {
  drawRoundedRect(doc, x, y, w, h, 3, WHITE, SLATE_200);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(SLATE_500);
  doc.text(title.toUpperCase(), x + 4, y + 6);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(SLATE_900);
  doc.text(value, x + 4, y + 14);

  let hintX = x + 4;
  if (delta) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(delta.positive ? EMERALD : ROSE);
    doc.text(delta.value, hintX, y + h - 4);
    hintX += doc.getTextWidth(delta.value) + 2;
  }
  if (hint) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(SLATE_500);
    doc.text(hint, hintX, y + h - 4);
  }
}

export async function generateDashboardPdf(params: GenerateDashboardPdfParams): Promise<void> {
  const { sales, totalUsers, totalCustomers, totalServices, month, year, chartRefs, t } = params;
  const s = sales.summary;
  const prev = sales.previousMonth;
  const monthLabel = `${t(`common.months.${month}`)} ${year}`;

  const [logoData, topServicesImg, paymentPieImg, staffImg, paymentBdImg] = await Promise.all([
    loadImageAsDataUrl('/logo192.png'),
    captureChart(chartRefs.topServices),
    captureChart(chartRefs.paymentPie),
    captureChart(chartRefs.staffPerformance),
    captureChart(chartRefs.paymentBreakdown),
  ]);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const contentW = pageW - margin * 2;
  let y = margin;

  // Background
  doc.setFillColor(SLATE_50);
  doc.rect(0, 0, pageW, pageH, 'F');

  // Header
  drawRoundedRect(doc, margin, y, contentW, 18, 3, WHITE, SLATE_200);
  if (logoData) {
    try {
      doc.addImage(logoData, 'PNG', margin + 4, y + 3, 12, 12);
    } catch {
      // ignore logo load failure
    }
  }
  const titleX = logoData ? margin + 20 : margin + 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(SLATE_900);
  doc.text(t('dashboard.pdf.title'), titleX, y + 8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(RED);
  doc.text(monthLabel, titleX, y + 14);
  y += 22;

  // Primary stats row
  const gap = 3;
  const cardW = (contentW - gap * 3) / 4;
  const cardH = 22;
  const primaryStats: Array<{
    title: string;
    value: string;
    hint?: string;
    delta?: { value: string; positive: boolean } | null;
  }> = [
    {
      title: t('dashboard.stats.revenue'),
      value: formatPrice(s.revenue),
      hint: t('dashboard.stats.vsLastMonth'),
      delta: deltaPercent(s.revenue, prev.revenue),
    },
    {
      title: t('dashboard.stats.bookings'),
      value: String(s.bookingCount),
      hint: t('dashboard.stats.completed'),
      delta: deltaPercent(s.bookingCount, prev.bookingCount),
    },
    {
      title: t('dashboard.stats.netProfit'),
      value: formatPrice(s.netProfit),
      hint: s.margin != null ? t('dashboard.stats.marginSuffix', { value: s.margin.toFixed(1) }) : undefined,
      delta: deltaPercent(s.netProfit, prev.netProfit),
    },
    {
      title: t('dashboard.stats.expenses'),
      value: formatPrice(s.expenses),
      hint: t('dashboard.stats.itemsSuffix', { count: s.expenseCount }),
      delta: deltaPercent(s.expenses, prev.expenses),
    },
  ];

  primaryStats.forEach((stat, i) => {
    drawStatCard(doc, margin + i * (cardW + gap), y, cardW, cardH, stat.title, stat.value, stat.hint, stat.delta);
  });
  y += cardH + 3;

  // Secondary stats row
  const secondaryStats = [
    { title: t('dashboard.stats.avgBookingValue'), value: formatPrice(s.averageBookingValue) },
    { title: t('dashboard.stats.commission'), value: formatPrice(s.commission), hint: t('dashboard.stats.entriesSuffix', { count: s.commissionCount }) },
    { title: t('dashboard.stats.customers'), value: totalCustomers.toLocaleString() },
    { title: t('dashboard.stats.staff'), value: String(totalUsers), hint: t('dashboard.stats.servicesSuffix', { count: totalServices }) },
  ];
  secondaryStats.forEach((stat, i) => {
    drawStatCard(doc, margin + i * (cardW + gap), y, cardW, cardH, stat.title, stat.value, stat.hint);
  });
  y += cardH + 5;

  // Charts row 1: Top Services (2/3) + Payment Pie (1/3)
  const chartRowH = 55;
  const leftChartW = (contentW - gap) * (2 / 3);
  const rightChartW = (contentW - gap) * (1 / 3);

  drawRoundedRect(doc, margin, y, leftChartW, chartRowH, 3, WHITE, SLATE_200);
  drawRoundedRect(doc, margin + leftChartW + gap, y, rightChartW, chartRowH, 3, WHITE, SLATE_200);

  if (topServicesImg) {
    const imgPad = 2;
    const imgW = leftChartW - imgPad * 2;
    const imgH = chartRowH - imgPad * 2;
    doc.addImage(topServicesImg, 'PNG', margin + imgPad, y + imgPad, imgW, imgH);
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(SLATE_500);
    doc.text(t('dashboard.pdf.topServicesNoData'), margin + 6, y + chartRowH / 2);
  }

  if (paymentPieImg) {
    const imgPad = 2;
    const imgW = rightChartW - imgPad * 2;
    const imgH = chartRowH - imgPad * 2;
    doc.addImage(paymentPieImg, 'PNG', margin + leftChartW + gap + imgPad, y + imgPad, imgW, imgH);
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(SLATE_500);
    doc.text(t('dashboard.pdf.revenueByPaymentNoData'), margin + leftChartW + gap + 6, y + chartRowH / 2);
  }
  y += chartRowH + 3;

  // Charts row 2: Staff + Payment Breakdown (50/50)
  const halfW = (contentW - gap) / 2;
  const chartRow2H = 48;

  drawRoundedRect(doc, margin, y, halfW, chartRow2H, 3, WHITE, SLATE_200);
  drawRoundedRect(doc, margin + halfW + gap, y, halfW, chartRow2H, 3, WHITE, SLATE_200);

  if (staffImg) {
    const imgPad = 2;
    doc.addImage(staffImg, 'PNG', margin + imgPad, y + imgPad, halfW - imgPad * 2, chartRow2H - imgPad * 2);
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(SLATE_500);
    doc.text(t('dashboard.pdf.staffPerformanceNoData'), margin + 6, y + chartRow2H / 2);
  }

  if (paymentBdImg) {
    const imgPad = 2;
    doc.addImage(
      paymentBdImg,
      'PNG',
      margin + halfW + gap + imgPad,
      y + imgPad,
      halfW - imgPad * 2,
      chartRow2H - imgPad * 2,
    );
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(SLATE_500);
    doc.text(t('dashboard.pdf.paymentBreakdownNoData'), margin + halfW + gap + 6, y + chartRow2H / 2);
  }

  // Footer
  const now = new Date();
  const generated = now.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(SLATE_500);
  doc.text(t('dashboard.pdf.generatedOn', { datetime: generated }), pageW / 2, pageH - 6, { align: 'center' });

  const filename = `BatamX_Report_${t(`common.months.${month}`)}_${year}.pdf`;
  doc.save(filename);
}
